/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClearableField, SetPlatformSettingsPayload } from "./types";
import {
  getPlatformSettings,
  setCampaignNotifiedToEmail,
  setPlatformSettings,
} from "./service";
import { emailSchema, platformSettingsSchema } from "./validationSchema";
import { scrollToTop, scrollToTopWithRadixScrollArea } from "helpers";
import { getUserDetails } from "service/service";
import lang from "lang";

export function usePlatformSettings() {
  const user = JSON.parse(localStorage.getItem("user")!);
  const { platformSettings: platformSettingsCopy } = lang;
  const [calendarUrl, setCalendarUrl] = useState("");
  const [autoRespond, setAutoRespond] = useState(false);
  const [interactionWebhook, setInteractionWebhook] = useState("");
  const [dealWebhook, setDealWebhook] = useState("");

  const [notifiedToEmail, setNotifiedToEmail] = useState("");
  const [initialNotifiedToEmail, setInitialNotifiedToEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const [customFields, setCustomFields] = useState<
    { name: string; value: string }[]
  >([
    { name: "", value: "" },
    { name: "", value: "" },
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const initialSettingsRef = useRef<SetPlatformSettingsPayload | null>(null);

  useEffect(() => {
    if (showSuccessAlert) {
      requestAnimationFrame(() => {
        scrollToTopWithRadixScrollArea();
      });
    }
  }, [showSuccessAlert]);

  useEffect(() => {
    setNotifiedToEmail(user?.notifyTo ? user?.notifyTo : user?.email);
    setInitialNotifiedToEmail(user?.notifyTo ? user?.notifyTo : user?.email);
  }, [user?.notifyTo, user?.email]);

  const buildPayload = useCallback(
    (): SetPlatformSettingsPayload => ({
      userID: user?.uid,
      calendlyLink: calendarUrl.trim() || null,
      autoRepliesToCampaign: autoRespond,
      interactionWebhookUrl: interactionWebhook.trim() || null,
      dealWebhookUrl: dealWebhook.trim() || null,
      customField1Name: customFields[0]?.name.trim() || null,
      customField1Value: customFields[0]?.value.trim() || null,
      customField2Name: customFields[1]?.name.trim() || null,
      customField2Value: customFields[1]?.value.trim() || null,
    }),
    [
      user?.uid,
      calendarUrl,
      autoRespond,
      interactionWebhook,
      dealWebhook,
      customFields,
    ]
  );

  const fetchSettings = useCallback(async () => {
    if (!user?.uid) return;

    try {
      setIsLoading(true);
      const data = await getPlatformSettings(user.uid);

      setCalendarUrl(data.calendlyLink ?? "");
      setInteractionWebhook(data.interactionWebhookUrl ?? "");
      setDealWebhook(data.dealWebhookUrl ?? "");

      const normalizedAutoRespond =
        data.autoRepliesToCampaign === true ||
        String(data.autoRepliesToCampaign) === "true";
      setAutoRespond(normalizedAutoRespond);

      setCustomFields([
        {
          name: data.customField1Name ?? "",
          value: data.customField1Value ?? "",
        },
        {
          name: data.customField2Name ?? "",
          value: data.customField2Value ?? "",
        },
      ]);

      initialSettingsRef.current = {
        userID: user?.uid,
        calendlyLink: data.calendlyLink ?? null,
        autoRepliesToCampaign: normalizedAutoRespond,
        interactionWebhookUrl: data.interactionWebhookUrl ?? null,
        dealWebhookUrl: data.dealWebhookUrl ?? null,
        customField1Name: data.customField1Name ?? null,
        customField1Value: data.customField1Value ?? null,
        customField2Name: data.customField2Name ?? null,
        customField2Value: data.customField2Value ?? null,
      };
    } catch (error) {
      console.error("Failed to fetch platform settings:", error);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const validate = async () => {
      try {
        await platformSettingsSchema.validate(
          {
            calendlyLink: calendarUrl || null,
            interactionWebhookUrl: interactionWebhook || null,
            dealWebhookUrl: dealWebhook || null,
          },
          { abortEarly: false }
        );
        setErrors({});
      } catch (err: any) {
        const nextErrors: Record<string, string> = {};
        err.inner?.forEach((e: any) => {
          if (e.path) nextErrors[e.path] = e.message;
        });
        setErrors(nextErrors);
      }
    };

    validate();
  }, [calendarUrl, interactionWebhook, dealWebhook]);

  const handleEmailChange = async (email: string) => {
    setNotifiedToEmail(email);
    try {
      await emailSchema.validate(email.trim());
      setEmailError(null);
    } catch (err: any) {
      setEmailError(err.message);
    }
  };

  const hasEmailChanged = useMemo(
    () => notifiedToEmail.trim() !== initialNotifiedToEmail.trim(),
    [notifiedToEmail, initialNotifiedToEmail]
  );

  const hasPlatformSettingsChanged = useCallback(() => {
    if (!initialSettingsRef.current) return false;

    const current = buildPayload();
    const initial = initialSettingsRef.current;

    return (
      current.calendlyLink !== initial.calendlyLink ||
      current.autoRepliesToCampaign !== initial.autoRepliesToCampaign ||
      current.interactionWebhookUrl !== initial.interactionWebhookUrl ||
      current.dealWebhookUrl !== initial.dealWebhookUrl ||
      current.customField1Name !== initial.customField1Name ||
      current.customField1Value !== initial.customField1Value ||
      current.customField2Name !== initial.customField2Name ||
      current.customField2Value !== initial.customField2Value
    );
  }, [buildPayload]);

  const handleAutoRespondToggle = (checked: boolean) => {
    setAutoRespond(checked);
  };

  const clearFieldAndSave = async (field: ClearableField) => {
    try {
      setIsLoading(true);

      if (field === "calendlyLink") setCalendarUrl("");
      if (field === "interactionWebhookUrl") setInteractionWebhook("");
      if (field === "dealWebhookUrl") setDealWebhook("");

      const payload: SetPlatformSettingsPayload = {
        userID: user.uid,
        calendlyLink:
          field === "calendlyLink" ? null : calendarUrl.trim() || null,
        interactionWebhookUrl:
          field === "interactionWebhookUrl"
            ? null
            : interactionWebhook.trim() || null,
        dealWebhookUrl:
          field === "dealWebhookUrl" ? null : dealWebhook.trim() || null,
        autoRepliesToCampaign: autoRespond,

        // customField1Name:
        //   field === "customField1"
        //     ? null
        //     : customFields[0]?.name.trim() || null,
        // customField1Value:
        //   field === "customField1"
        //     ? null
        //     : customFields[0]?.value.trim() || null,
        // customField2Name:
        //   field === "customField2"
        //     ? null
        //     : customFields[1]?.name.trim() || null,
        // customField2Value:
        //   field === "customField2"
        //     ? null
        //     : customFields[1]?.value.trim() || null,
      };

      await setPlatformSettings(payload);
      await fetchSettings();

      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 5000);
    } catch (error) {
      console.error("Failed to clear field:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      if (emailError) return;

      setIsLoading(true);

      const promises: Promise<any>[] = [];

      const platformChanged = hasPlatformSettingsChanged();
      const emailChanged =
        notifiedToEmail.trim() !== initialNotifiedToEmail.trim();

      if (platformChanged) {
        promises.push(setPlatformSettings(buildPayload()));
      }

      if (emailChanged) {
        promises.push(
          setCampaignNotifiedToEmail({
            notifyTo: notifiedToEmail.trim(),
          })
        );
      }

      if (promises.length === 0) {
        setIsLoading(false);
        return;
      }

      await Promise.all(promises);

      if (platformChanged) {
        await fetchSettings();
      }
      if (emailChanged) {
        await getUserDetails();
        setInitialNotifiedToEmail(notifiedToEmail.trim());
      }

      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
    } catch (error) {
      console.error("Failed to save platform settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateCustomField = (
    index: number,
    key: "name" | "value",
    value: string
  ) => {
    const updated = [...customFields];
    updated[index][key] = value;
    setCustomFields(updated);
  };

  const hasValidationErrors = Object.keys(errors).length > 0;

  const isSaveDisabled = useMemo(() => {
    return (
      !!emailError ||
      hasValidationErrors ||
      (hasEmailChanged && !notifiedToEmail.trim()) ||
      (!hasEmailChanged && !hasPlatformSettingsChanged())
    );
  }, [
    notifiedToEmail,
    hasEmailChanged,
    hasPlatformSettingsChanged,
    emailError,
    hasValidationErrors,
  ]);

  return {
    calendarUrl,
    setCalendarUrl,
    autoRespond,
    handleAutoRespondToggle,
    interactionWebhook,
    setInteractionWebhook,
    dealWebhook,
    setDealWebhook,
    isSaveDisabled,
    isLoading,
    saveSettings,
    errors,
    showSuccessAlert,
    clearFieldAndSave,
    customFields,
    updateCustomField,
    notifiedToEmail,
    setNotifiedToEmail,
    emailError,
    handleEmailChange,
    platformSettingsCopy,
  };
}
